import React, {useState, useCallback, useEffect} from 'react'
import {
    Box,
    Button,
    Stack,
    SimpleGrid,
    useMultiStyleConfig,
    useBreakpointValue,
    Alert,
    AlertDescription,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useForm} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHistory} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import Field from '../../../field'
import useOrderStatusFields from '../../../forms/useOrderStatusFields'
import {
    OrderHeading,
    OrderStatus,
    OrderProducts,
    OrderPayment,
    ManageOrder,
    OrderDetailsSummary,
    OrderTracking,
    ReturnAlert,
    ReturnOrderPolicyAlert
} from '../../../../pages/account/partials'
import {ORDER_RAW_STATUS, ORDER_PRODUCTS_RENDER_MODES} from '../../../../pages/account/constant'

const OrderStatusFormPlaceholderBlock = ({CtaButtonText, ...props}) => {
    const intl = useIntl()
    const global = useMultiStyleConfig('GlobalCMS')
    const history = useHistory()
    const {site} = useMultiSite()
    const [isLoading, setIsLoading] = useState(false)
    const [orderData, setOrderData] = useState(null)
    const [serverError, setServerError] = useState(null)
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const config = getConfig()
    const {currency} = useCurrency()
    const isMobile = useBreakpointValue({base: true, lg: false})

    // Add/remove class to body to hide other content when order is displayed
    useEffect(() => {
        if (orderData) {
            document.body.classList.add('order-details-active')
        } else {
            document.body.classList.remove('order-details-active')
        }

        return () => {
            document.body.classList.remove('order-details-active')
        }
    }, [orderData])

    const form = useForm({
        mode: 'onBlur',
        defaultValues: {
            orderNumber: '',
            orderEmail: '',
            billingPostcode: ''
        }
    })

    const {
        control,
        handleSubmit,
        formState: {errors},
        watch
    } = form

    // Get field definitions from the custom hook
    const fields = useOrderStatusFields({form})

    // Clear server error when user modifies any field
    useEffect(() => {
        const subscription = watch(() => {
            if (serverError) {
                setServerError(null)
            }
        })
        return () => subscription.unsubscribe()
    }, [watch, serverError])

    const onSubmit = useCallback(async (data) => {
        setIsLoading(true)
        setServerError(null) // Clear any previous errors

        try {
            const token = await getTokenWhenReady()
            const {proxyPath, parameters} = config.app.commerceAPI
            const organizationId = parameters.organizationId

            // Construct API URL
            const url = `${appOrigin}${proxyPath}/custom/order-details-guest/v1/organizations/${organizationId}/order-details-guest?siteId=${site.id}`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    orderNumber: data.orderNumber,
                    orderEmail: data.orderEmail,
                    postalCode: data.billingPostcode
                })
            })

            if (!response.ok) {
                throw new Error('Order not found')
            }

            const result = await response.json()

            setIsLoading(false)

            // Store order data to display on the same page
            if (result?.order) {
                setOrderData(result.order)
                setServerError(null)
            } else {
                // No order found
                setServerError(intl.formatMessage({
                    defaultMessage: 'Invalid order number, email or postcode.',
                    id: 'order_status_form.error.invalid_combination'
                }))
            }
        } catch (error) {
            setIsLoading(false)
            setServerError(intl.formatMessage({
                defaultMessage: 'Invalid order number, email or postcode.',
                id: 'order_status_form.error.invalid_combination'
            }))
            console.error('Order lookup error:', error)
        }
    }, [getTokenWhenReady, config, appOrigin, site.id, intl, history])

    // If order data is loaded, display order details
    if (orderData) {
        const shouldShowTracking = ORDER_RAW_STATUS.TRACKING.includes(
            orderData.statusRaw?.toLowerCase()
        )
        const getOrderProductsMode = (order) =>
            ORDER_RAW_STATUS.TRACKING.includes(order.statusRaw?.toLowerCase()) &&
            order.packages?.length > 0
                ? ORDER_PRODUCTS_RENDER_MODES.PACKAGES
                : ORDER_PRODUCTS_RENDER_MODES.ITEMS
        const mode = getOrderProductsMode(orderData)
        const orderProductsData =
            mode === ORDER_PRODUCTS_RENDER_MODES.PACKAGES ? orderData : orderData.items

        return (
            <>
                {/* CSS to hide other page content */}
                <style
                    dangerouslySetInnerHTML={{
                        __html: `
                    body.order-details-active [data-testid="page-header"],
                    body.order-details-active [data-testid="footer"],
                    body.order-details-active [data-block-type="LandingPage"] [data-block-type]:not([data-block-type="OrderStatusFormPlaceholderBlock"]):not([data-block-type="OrderStatusFormPlaceholderBlock-OrderView"]) {
                        display: none !important;
                    }
                `}} />

                <Box
                    data-block-type="OrderStatusFormPlaceholderBlock-OrderView"
                    bg="white"
                    minH="100vh"
                    py={{base: 6, lg: 10}}
                >
                    <Box maxW="1200px" mx="auto">
                        <Stack spacing={6}>
                            <SimpleGrid columns={{base: 1, lg: 2}} spacing={{base: 6, lg: '120px'}}>
                                <Box>
                                    <Stack>
                                        <OrderHeading
                                            order={orderData}
                                            isLoading={false}
                                            onBackClick={() => setOrderData(null)}
                                        />
                                        {!isMobile && (
                                            <>
                                                <ReturnAlert orderItems={orderData.items}/>
                                                <ReturnOrderPolicyAlert
                                                    {...orderData.returnableStatus}
                                                    containerProps={{mb: 6}}
                                                />
                                            </>
                                        )}
                                        {shouldShowTracking ? (
                                            <OrderTracking order={orderData} />
                                        ) : (
                                            <OrderStatus order={orderData} />
                                        )}
                                        {isMobile && (
                                            <ManageOrder
                                                containerProps={{mt: 2}}
                                                dividerProps={{mb: 4}}
                                                {...orderData.returnableStatus}
                                                returnLabel={orderData.returnLabel}
                                            />
                                        )}
                                        <OrderProducts
                                            orderBrand={orderData.orderBrand}
                                            orderProductsData={orderProductsData}
                                            currency={currency}
                                            mode={mode}
                                        />
                                        <OrderPayment order={orderData} />
                                    </Stack>
                                </Box>
                                <Box maxW={{base: 'full', lg: '370px'}}>
                                    <Box display={{base: 'none', lg: 'block'}}>
                                        <ManageOrder
                                            {...orderData.returnableStatus}
                                            returnLabel={orderData.returnLabel}
                                        />
                                    </Box>
                                    <OrderDetailsSummary {...orderData} currency={currency} />
                                </Box>
                            </SimpleGrid>
                        </Stack>
                    </Box>
                </Box>
            </>
        )
    }

    // Otherwise, show the form
    return (
        <Box
            as="form"
            onSubmit={handleSubmit(onSubmit)}
            {...global.notFullBleed}
            data-block-type="OrderStatusFormPlaceholderBlock"
            mb="157px"
            {...props}
            px={0}
            textTransform="none"
        >
            <Stack spacing={{base: 4, md: 6}} w="100%">
                {/* Server Error Banner */}
                {serverError && (
                    <Alert variant="warning" status="error" mb={8}>
                        <AlertDescription flex="1">{serverError}</AlertDescription>
                    </Alert>
                )}

                {/* Order Number Field */}
                <Field {...fields.orderNumber} />

                {/* Order Email Field */}
                <Field {...fields.orderEmail} />

                {/* Billing Postcode Field */}
                <Field {...fields.billingPostcode} />

                {/* Submit Button */}
                <Button
                    type="submit"
                    variant="solid"
                    size="lg"
                    width={{base: '100%', lg: '203px'}}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    spinner={isLoading ? undefined : null}
                    mt={4}
                >
                    {!isLoading &&
                        (CtaButtonText ||
                            intl.formatMessage({
                                defaultMessage: 'Track Order',
                                id: 'order_status_form.button.submit'
                            }))}
                </Button>
            </Stack>
        </Box>
    )
}

OrderStatusFormPlaceholderBlock.propTypes = {
    CtaButtonText: PropTypes.string
}

export default OrderStatusFormPlaceholderBlock
